import { useEffect, useState } from "react";
import { Eye, EyeOff, Pencil, X } from "lucide-react";
import { Field, TextInput, TextArea, SelectInput } from "@/components/brand/Field";
import { api, formatApiError } from "@/lib/api";

const linesToList = (s) => s.split("\n").map((l) => l.trim()).filter(Boolean);
const listToLines = (l) => (l || []).join("\n");
const workflowToLines = (w) => (w || []).map((s) => `${s.title} | ${s.text}`).join("\n");
const linesToWorkflow = (s) =>
  linesToList(s).map((l) => {
    const [title, ...rest] = l.split("|");
    return { title: title.trim(), text: (rest.join("|") || title).trim() };
  });
const faqsToText = (f) => (f || []).map((x) => `Q: ${x.question}\nA: ${x.answer}`).join("\n\n");
const textToFaqs = (s) =>
  s.split(/\n\s*\n/).map((block) => {
    const q = block.match(/Q:\s*(.+)/)?.[1]?.trim();
    const a = block.match(/A:\s*([\s\S]+)/)?.[1]?.trim();
    return q && a ? { question: q, answer: a } : null;
  }).filter(Boolean);

const Editor = ({ service, onClose, onSaved }) => {
  const [f, setF] = useState({
    name: service.name,
    intro: service.intro,
    overview: service.overview,
    cta_text: service.cta_text,
    order: service.order,
    status: service.status,
    seo_title: service.seo?.title || "",
    seo_description: service.seo?.description || "",
    problems: listToLines(service.problems),
    features: listToLines(service.features),
    deliverables: listToLines(service.deliverables),
    benefits: listToLines(service.benefits),
    workflow: workflowToLines(service.workflow),
    faqs: faqsToText(service.faqs),
  });
  const [state, setState] = useState("idle");
  const [error, setError] = useState("");

  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    setState("saving");
    setError("");
    try {
      const payload = {
        name: f.name,
        intro: f.intro,
        overview: f.overview,
        cta_text: f.cta_text,
        order: Number(f.order) || 0,
        status: f.status,
        seo: { title: f.seo_title, description: f.seo_description },
        problems: linesToList(f.problems),
        features: linesToList(f.features),
        deliverables: linesToList(f.deliverables),
        benefits: linesToList(f.benefits),
        workflow: linesToWorkflow(f.workflow),
        faqs: textToFaqs(f.faqs),
      };
      const r = await api.put(`/admin/services/${service.id}`, payload);
      onSaved(r.data);
    } catch (err) {
      setState("error");
      setError(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-center bg-brand-black/70 p-0 backdrop-blur-sm sm:p-6" onClick={onClose} data-testid="service-editor-modal">
      <form onSubmit={save} className="h-full w-full max-w-3xl space-y-6 overflow-y-auto border border-brand-mist/15 bg-brand-charcoal p-6 sm:h-auto sm:max-h-[92vh] sm:rounded-2xl sm:p-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="eyebrow">Edit service</p>
            <h2 className="mt-1 font-display text-xl font-bold text-brand-snow">{service.name}</h2>
            <p className="mt-0.5 text-xs text-brand-mist">Slug and category are fixed: {service.slug}</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" data-testid="service-editor-close" className="rounded-md p-2 text-brand-mist hover:text-brand-snow"><X size={18} /></button>
        </div>
        <div className="grid gap-5 sm:grid-cols-2">
          <Field label="Name" htmlFor="se-name" required><TextInput id="se-name" testid="se-name" value={f.name} onChange={set("name")} required /></Field>
          <Field label="CTA wording" htmlFor="se-cta"><TextInput id="se-cta" testid="se-cta" value={f.cta_text} onChange={set("cta_text")} /></Field>
          <Field label="Display order" htmlFor="se-order"><TextInput id="se-order" type="number" testid="se-order" value={f.order} onChange={set("order")} /></Field>
          <Field label="Status" htmlFor="se-status">
            <SelectInput id="se-status" testid="se-status" value={f.status} onChange={set("status")}>
              <option value="published">Published</option>
              <option value="draft">Draft</option>
              <option value="archived">Archived</option>
            </SelectInput>
          </Field>
        </div>
        <Field label="Introduction" htmlFor="se-intro" required><TextArea id="se-intro" rows={2} testid="se-intro" value={f.intro} onChange={set("intro")} required /></Field>
        <Field label="Overview" htmlFor="se-overview" required><TextArea id="se-overview" rows={4} testid="se-overview" value={f.overview} onChange={set("overview")} required /></Field>
        <div className="grid gap-5 sm:grid-cols-2">
          <Field label="Problems (one per line)" htmlFor="se-problems"><TextArea id="se-problems" rows={4} testid="se-problems" value={f.problems} onChange={set("problems")} /></Field>
          <Field label="Features (one per line)" htmlFor="se-features"><TextArea id="se-features" rows={4} testid="se-features" value={f.features} onChange={set("features")} /></Field>
          <Field label="Deliverables (one per line)" htmlFor="se-deliverables"><TextArea id="se-deliverables" rows={4} testid="se-deliverables" value={f.deliverables} onChange={set("deliverables")} /></Field>
          <Field label="Benefits (one per line)" htmlFor="se-benefits"><TextArea id="se-benefits" rows={4} testid="se-benefits" value={f.benefits} onChange={set("benefits")} /></Field>
        </div>
        <Field label="Workflow (Title | text, one per line)" htmlFor="se-workflow"><TextArea id="se-workflow" rows={4} testid="se-workflow" value={f.workflow} onChange={set("workflow")} /></Field>
        <Field label="FAQs (Q: … / A: …, blank line between)" htmlFor="se-faqs"><TextArea id="se-faqs" rows={6} testid="se-faqs" value={f.faqs} onChange={set("faqs")} /></Field>
        <div className="grid gap-5 sm:grid-cols-2">
          <Field label="SEO title" htmlFor="se-seo-title"><TextInput id="se-seo-title" testid="se-seo-title" value={f.seo_title} onChange={set("seo_title")} /></Field>
          <Field label="SEO description" htmlFor="se-seo-desc"><TextInput id="se-seo-desc" testid="se-seo-desc" value={f.seo_description} onChange={set("seo_description")} /></Field>
        </div>
        {state === "error" && <p role="alert" className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300" data-testid="service-editor-error">{error}</p>}
        <div className="flex gap-3">
          <button type="submit" disabled={state === "saving"} data-testid="service-editor-save" className="rounded-full bg-brand-emerald px-6 py-3 font-display text-sm font-semibold text-brand-black transition-colors hover:bg-brand-snow disabled:opacity-60">
            {state === "saving" ? "Saving…" : "Save changes"}
          </button>
          <button type="button" onClick={onClose} className="rounded-full border border-brand-mist/25 px-6 py-3 text-sm font-medium text-brand-mist hover:text-brand-snow">Cancel</button>
        </div>
      </form>
    </div>
  );
};

const AdminServices = () => {
  const [services, setServices] = useState([]);
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState("");

  const load = () => api.get("/admin/services").then((r) => setServices(r.data)).catch(() => setError("Could not load services."));
  useEffect(() => { load(); }, []);

  const toggle = async (svc) => {
    const next = svc.status === "published" ? "draft" : "published";
    await api.post(`/admin/services/${svc.id}/status?status=${next}`);
    setServices((list) => list.map((s) => (s.id === svc.id ? { ...s, status: next } : s)));
  };

  const onSaved = (updated) => {
    setServices((list) => list.map((s) => (s.id === updated.id ? updated : s)));
    setEditing(null);
  };

  return (
    <div className="space-y-6" data-testid="admin-services">
      <div className="space-y-1">
        <h1 className="font-display text-2xl font-bold text-brand-snow sm:text-3xl">Services</h1>
        <p className="text-sm text-brand-mist">{services.length} services across the catalogue. Publish, unpublish or edit copy.</p>
      </div>
      {error && <p className="text-sm text-red-400">{error}</p>}
      <div className="surface-card overflow-x-auto" data-testid="services-table">
        <table className="w-full min-w-[680px] text-left text-sm">
          <thead>
            <tr className="border-b border-brand-mist/10 text-xs uppercase tracking-wider text-brand-mist">
              <th className="px-5 py-3.5 font-medium">Service</th>
              <th className="px-5 py-3.5 font-medium">Category</th>
              <th className="px-5 py-3.5 font-medium">Status</th>
              <th className="px-5 py-3.5 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-brand-mist/8">
            {services.map((s) => (
              <tr key={s.id} data-testid={`service-row-${s.slug}`} className="transition-colors hover:bg-white/5">
                <td className="px-5 py-3.5">
                  <p className="font-medium text-brand-snow">{s.name}</p>
                  <p className="text-xs text-brand-mist">/{s.category}/{s.slug}</p>
                </td>
                <td className="px-5 py-3.5 text-brand-mist">{s.category}</td>
                <td className="px-5 py-3.5">
                  <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${s.status === "published" ? "text-brand-emerald" : s.status === "draft" ? "text-yellow-300" : "text-brand-mist"}`}>
                    {s.status === "published" ? <Eye size={13} /> : <EyeOff size={13} />} {s.status}
                  </span>
                </td>
                <td className="px-5 py-3.5">
                  <div className="flex gap-2">
                    <button onClick={() => setEditing(s)} data-testid={`service-edit-${s.slug}`} className="inline-flex items-center gap-1.5 rounded-full border border-brand-mist/25 px-3 py-1.5 text-xs font-medium text-brand-snow hover:border-brand-emerald hover:text-brand-emerald">
                      <Pencil size={12} /> Edit
                    </button>
                    <button onClick={() => toggle(s)} data-testid={`service-toggle-${s.slug}`} className="rounded-full border border-brand-mist/25 px-3 py-1.5 text-xs font-medium text-brand-mist hover:border-brand-azure hover:text-brand-azure">
                      {s.status === "published" ? "Unpublish" : "Publish"}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editing && <Editor service={editing} onClose={() => setEditing(null)} onSaved={onSaved} />}
    </div>
  );
};

export default AdminServices;
